import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { db, storage } from "../Auth/firebase";
import { HiOutlineUserGroup } from "react-icons/hi";
import { BsFillFileEarmarkTextFill } from "react-icons/bs";
import { FaCommentDots, FaTrashAlt } from "react-icons/fa";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { useAuth } from "../../context/AuthContext";
import ConfirmModal from "../../Modals/ProjectDelModal";
import { Button, Input } from "reactstrap";

const MAX_FILE_SIZE_MB = 10;

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [clients, setClients] = useState([]); // New State for Clients
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [typedProjectName, setTypedProjectName] = useState("");
  const { currentUser } = useAuth();
  const [completedTasks, setCompletedTasks] = useState(0);
  const [totalTasks, setTotalTasks] = useState(0);
  const [completionRate, setCompletionRate] = useState(0);

  useEffect(() => {
    const fetchProject = async () => {
      if (projectId) {
        const projectRef = doc(collection(db, "projects"), projectId);
        const projectSnapshot = await getDoc(projectRef);

        if (projectSnapshot.exists()) {
          setProject({ id: projectSnapshot.id, ...projectSnapshot.data() });
        }
      }
    };

    fetchProject();
  }, [projectId]);

  // Project Progress Stats
  useEffect(() => {
    if (!project) return;

    const fetchTaskStats = () => {
      const projectId = project.id;
      const projectRef = doc(db, "projects", projectId);
      const boardsCollection = collection(projectRef, "boards");

      const unsubscribeBoards = onSnapshot(
        boardsCollection,
        (boardsSnapshot) => {
          let completed = 0;
          let incomplete = 0;

          boardsSnapshot.forEach((boardDoc) => {
            const boardId = boardDoc.id;
            const tasksCollection = collection(
              db,
              `projects/${projectId}/boards/${boardId}/tasks`
            );

            onSnapshot(tasksCollection, (tasksSnapshot) => {
              tasksSnapshot.forEach((taskDoc) => {
                const taskData = taskDoc.data();
                taskData.completed ? completed++ : incomplete++;
              });

              const total = completed + incomplete;
              setCompletedTasks(completed);
              setTotalTasks(total);
              setCompletionRate(
                total > 0 ? Math.round((completed / total) * 100) : 0
              );
            });
          });
        }
      );

      return () => unsubscribeBoards();
    };

    fetchTaskStats();
  }, [project]);

  useEffect(() => {
    if (project) {
      const fetchAssignees = async () => {
        const assigneePromises = project.assignees.map(async (uid) => {
          const userRef = doc(collection(db, "users"), uid);
          const userSnapshot = await getDoc(userRef);
          return userSnapshot.exists() ? { uid, ...userSnapshot.data() } : null;
        });
        const resolvedAssignees = await Promise.all(assigneePromises);
        setAssignees(resolvedAssignees.filter(Boolean));
      };

      const fetchClients = async () => {
        if (project.clients && project.clients.length > 0) {
          const clientPromises = project.clients.map(async (uid) => {
            const userRef = doc(collection(db, "users"), uid);
            const userSnapshot = await getDoc(userRef);
            return userSnapshot.exists()
              ? { uid, ...userSnapshot.data() }
              : null;
          });
          const resolvedClients = await Promise.all(clientPromises);
          setClients(resolvedClients.filter(Boolean));
        }
      };

      fetchAssignees();
      fetchClients(); // Fetch clients
    }
  }, [project]);

  useEffect(() => {
    if (project) {
      const qComments = query(
        collection(db, "comments"),
        where("projectId", "==", project.id)
      );
      const unsubscribeComments = onSnapshot(qComments, (snapshot) => {
        const commentsList = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setComments(commentsList);
      });

      const qFiles = query(
        collection(db, "files"),
        where("projectId", "==", project.id)
      );
      const unsubscribeFiles = onSnapshot(qFiles, (snapshot) => {
        const filesList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setFiles(filesList);
      });

      return () => {
        unsubscribeComments();
        unsubscribeFiles();
      };
    }
  }, [project]);

  const handleAddComment = async () => {
    if (newComment.trim()) {
      await addDoc(collection(db, "comments"), {
        projectId: project.id,
        userId: currentUser.uid,
        userName: currentUser.displayName,
        profilePicture: currentUser.photoURL,
        text: newComment.trim(),
        timestamp: new Date().toISOString(),
      });
      setNewComment("");
    }
  };

  const handleDeleteComment = async (commentId) => {
    setConfirmMessage("Are you sure you want to delete this comment?");
    setConfirmAction(() => async () => {
      try {
        await deleteDoc(doc(db, "comments", commentId));
        setIsConfirmModalOpen(false);
      } catch (error) {
        console.error("Error deleting comment:", error);
      }
    });
    setIsConfirmModalOpen(true);
  };

  const handleDeleteFile = async (fileId, fileUrl) => {
    setConfirmMessage("Are you sure you want to delete this file?");
    setConfirmAction(() => async () => {
      try {
        const fileRef = ref(storage, fileUrl);
        await deleteObject(fileRef);
        await deleteDoc(doc(db, "files", fileId));
        setIsConfirmModalOpen(false);
      } catch (error) {
        console.error("Error deleting file:", error);
      }
    });
    setIsConfirmModalOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        alert(`File size exceeds the maximum limit of ${MAX_FILE_SIZE_MB}MB.`);
        return;
      }

      const storageRef = ref(storage, `projects/${project.id}/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      setUploading(true);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Error uploading file:", error);
          setUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          await addDoc(collection(db, "files"), {
            projectId: project.id,
            name: file.name,
            url: downloadURL,
            uploadedBy: currentUser.uid,
            uploadedByName: currentUser.displayName,
            uploadedByProfile: currentUser.photoURL,
            timestamp: new Date().toISOString(),
          });
          setUploading(false);
          setUploadProgress(0);
        }
      );
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (!project) {
    return <div>Loading...</div>;
  }

  const isTeamLead = currentUser && project.creator.uid === currentUser.uid;

  const handleEdit = () => {
    navigate(`/projects/edit-project/${project.id}`);
  };

  const handleDelete = async () => {
    setConfirmMessage("Are you sure you want to delete this project?");
    setConfirmAction(() => async () => {
      try {
        await deleteDoc(doc(db, "projects", project.id));
        setIsConfirmModalOpen(false);
        navigate("/projects");
      } catch (error) {
        console.error("Error deleting project:", error);
      }
    });
    setIsConfirmModalOpen(true);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow rounded-lg h-[90vh] overflow-y-auto scrollbar-none">
      <div className="flex flex-row justify-between align-center">
        <h2 className="text-3xl font-bold mb-4">{project.name}</h2>
        {isTeamLead && (
          <div className="flex justify-end space-x-4">
            <button
              onClick={handleEdit}
              className="flex items-center text-white bg-[#9999ff] hover:text-[#9999ff] hover:bg-white border border-[#9999ff] px-4 rounded-lg"
            >
              <FiEdit className="mr-1" />
              Edit
            </button>
            <button
              onClick={() => {
                setTypedProjectName("");
                handleDelete();
              }}
              className="flex items-center text-white bg-[#ff8080] hover:text-[#ff8080] hover:bg-white border border-[#ff8080] px-4 rounded-lg"
            >
              <FiTrash2 className="mr-1" />
              Delete
            </button>
          </div>
        )}
      </div>
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <h4 className="text-xl font-semibold">Project Progress</h4>
          <span className="text-lg font-bold">
            {completionRate}% Complete ({completedTasks}/{totalTasks} Tasks)
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-blue-600 h-4 rounded-full"
            style={{ width: `${completionRate}%` }}
          ></div>
        </div>
      </div>

      <p className="text-gray-600 mb-4">{project.description}</p>
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center bg-blue-100 p-4 rounded-lg">
          <div>
            <div className="flex items-center mb-2">
              <HiOutlineUserGroup className="text-blue-500 text-2xl mr-2" />
              <span className="block font-semibold">Assignees:</span>
            </div>
            <ul>
              {assignees.map((assignee) => (
                <li
                  key={assignee.uid}
                  className="flex items-center space-x-2 space-y-2 text-gray-700"
                >
                  <img
                    src={
                      assignee.profileImage || "https://via.placeholder.com/24"
                    }
                    alt={assignee.displayName}
                    className="w-6 h-6 rounded-full"
                  />
                  <span>
                    {assignee.displayName}{" "}
                    {assignee.uid === project.creator.uid && (
                      <span className="text-xs text-blue-500">(Team Lead)</span>
                    )}
                    {assignee.uid !== project.creator.uid && (
                      <span className="text-xs text-gray-500">
                        (Team Member)
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {clients.length > 0 && (
          <div className="flex items-center bg-green-100 p-4 rounded-lg">
            <div>
              <div className="flex items-center mb-2">
                <HiOutlineUserGroup className="text-green-500 text-2xl mr-2" />
                <span className="block font-semibold">Clients:</span>
              </div>
              <ul>
                {clients.map((client) => (
                  <li
                    key={client.uid}
                    className="flex items-center space-x-2 space-y-2 text-gray-700"
                  >
                    <img
                      src={
                        client.profileImage || "https://via.placeholder.com/24"
                      }
                      alt={client.displayName}
                      className="w-6 h-6 rounded-full"
                    />
                    <span>{client.displayName}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      <h3 className="text-xl font-semibold mt-6 mb-2">Comments</h3>
      <div className="flex flex-col bg-yellow-100 p-4 rounded-lg mb-4">
        <div className="flex items-center">
          <FaCommentDots className="text-[#ff8989] text-2xl mr-2" />
          <span className="font-semibold">Add Comment:</span>
        </div>
        <textarea
          rows={3}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Write your comment here..."
          className="w-full mt-2 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
        />
        <button
          onClick={handleAddComment}
          className="self-end mt-2 bg-[#ff8989] text-white px-4 py-2 rounded-lg hover:bg-yellow-600"
        >
          Submit
        </button>
      </div>
      {comments.length > 0 && (
        <ul className="mb-6">
          {comments.map((comment) => (
            <li key={comment.id} className="mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <img
                    src={
                      comment.profilePicture || "https://via.placeholder.com/24"
                    }
                    alt={comment.userName}
                    className="w-6 h-6 rounded-full"
                  />
                  <span className="font-semibold">{comment.userName}</span>
                  <span className="text-xs text-gray-500">
                    {formatTimestamp(comment.timestamp)}
                  </span>
                </div>
                {isTeamLead && (
                  <button
                    onClick={() => handleDeleteComment(comment.id)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <FaTrashAlt />
                  </button>
                )}
              </div>
              <p className="ml-8 mt-2 text-gray-600">{comment.text}</p>
            </li>
          ))}
        </ul>
      )}

      <h3 className="text-xl font-semibold mt-6 mb-2">Files</h3>
      <div className="flex flex-col bg-purple-100 p-4 rounded-lg mb-4">
        <div className="flex items-center mb-2">
          <BsFillFileEarmarkTextFill className="text-purple-500 text-2xl mr-2" />
          <span className="font-semibold">Upload File:</span>
        </div>
        <input
          type="file"
          onChange={handleFileUpload}
          accept="*"
          className="bg-white border border-purple-400 rounded-md p-2"
          disabled={uploading}
        />
        {uploading && (
          <div className="flex items-center mt-2">
            <div className="w-full bg-gray-200 rounded-full">
              <div
                className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
                style={{ width: `${uploadProgress}%` }}
              >
                {uploadProgress}%
              </div>
            </div>
          </div>
        )}
      </div>
      {files.length > 0 ? (
        <ul className="mb-6">
          {files.map((file) => (
            <li
              key={file.id}
              className="flex items-center justify-between space-x-4 mb-4"
            >
              <div className="flex items-center space-x-4">
                <BsFillFileEarmarkTextFill className="text-purple-500 text-2xl" />
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {file.name}
                </a>
                <span className="text-sm text-gray-500">
                  (Uploaded by {file.uploadedByName})
                </span>
              </div>
              {isTeamLead && (
                <button
                  onClick={() => handleDeleteFile(file.id, file.url)}
                  className="text-red-500 hover:text-red-600"
                >
                  <FaTrashAlt />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div>No files attached</div>
      )}

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onRequestClose={() => setIsConfirmModalOpen(false)}
        onConfirm={confirmAction}
        message={confirmMessage}
        confirmDisabled={
          typedProjectName.toLowerCase() !== project.name.toLowerCase() ||
          typedProjectName === ""
        }
        typedProjectName={typedProjectName}
        setTypedProjectName={setTypedProjectName}
        projectName={project.name}
      />
    </div>
  );
};

export default ProjectDetails;
